/**
 * Outside-process colours from the design system. Post-process names are free text,
 * so the colour comes from keywords in the name; anything unrecognised is neutral.
 */
export type ProcessKind = "paint" | "heat" | "coat" | "anodise" | "other";

const KEYWORDS: Array<[ProcessKind, RegExp]> = [
  ["coat", /powder|coat/i],
  ["paint", /paint|spray/i],
  ["heat", /heat|harden|temper|stress|anneal/i],
  ["anodise", /anodi[sz]|plat|chrom|zinc|passivat|black ?ox|finish/i],
];

export function processKind(name: string): ProcessKind {
  return KEYWORDS.find(([, re]) => re.test(name))?.[0] ?? "other";
}

/**
 * `tag` is the tint chip on cards, `dot` the marker, `solid` a bar in the base colour,
 * `pencil` the tint-and-dashed-border bar for quotes ("pencilled in"). `short` labels the drawer chip.
 */
export const PROCESS_META: Record<ProcessKind, { short: string | null; tag: string; dot: string; solid: string; pencil: string; ring: string }> = {
  paint: {
    short: "Paint",
    tag: "bg-paint-tint text-paint-text",
    dot: "bg-paint",
    solid: "bg-paint",
    pencil: "bg-paint-tint text-paint-text border-[1.5px] border-dashed border-paint",
    ring: "ring-paint-tint",
  },
  heat: {
    short: "Heat treat",
    tag: "bg-heat-tint text-heat-text",
    dot: "bg-heat",
    solid: "bg-heat",
    pencil: "bg-heat-tint text-heat-text border-[1.5px] border-dashed border-heat",
    ring: "ring-heat-tint",
  },
  coat: {
    short: "Powder coat",
    tag: "bg-coat-tint text-coat-text",
    dot: "bg-coat",
    solid: "bg-coat",
    pencil: "bg-coat-tint text-coat-text border-[1.5px] border-dashed border-coat",
    ring: "ring-coat-tint",
  },
  anodise: {
    short: "Anodise",
    tag: "bg-anodise-tint text-anodise-text",
    dot: "bg-anodise",
    solid: "bg-anodise",
    pencil: "bg-anodise-tint text-anodise-text border-[1.5px] border-dashed border-anodise",
    ring: "ring-anodise-tint",
  },
  other: {
    short: null,
    tag: "bg-surface text-muted",
    dot: "bg-quoted",
    solid: "bg-muted",
    pencil: "bg-surface text-muted border-[1.5px] border-dashed border-quoted",
    ring: "ring-surface",
  },
};
